import { PackageFilterModel } from 'ts/package/classes/PackageFilterModel';
import { Deal, Option } from 'ts/package/interfaces/BookPackageResponse';
import { PackageSearch, SearchDate } from 'ts/package/interfaces/Package';

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { i18nSharedConfig } from 'utils/shared/utils';
import { i18nConfig as i18nUiConfig } from 'utils/ui';
import { Pax } from 'ts/package/interfaces/PackageOfflineResponse';
import { PassengerType } from 'constants/package';
import { SyntheticEvent } from 'react';
import { FlightModel } from 'ts/package/interfaces/PackageDetails';

export interface i18nPackageConfigProps {
  enTranslation: any;
  heTranslation: any;
  esTranslation: any;
}

export const i18nPackageConfig = (props: i18nPackageConfigProps) => {
  const lang = localStorage.getItem('language');

  i18nSharedConfig({ ...props, ruTranslation: {} });
  i18nUiConfig(props);

  i18n.use(initReactI18next).init({
    resources: {
      en: {
        translation: props.enTranslation,
      },
      he: {
        translation: props.heTranslation,
      },
      es: {
        translation: props.esTranslation,
      },
    },
    lng: lang ? lang : 'he',
    fallbackLng: 'he',

    interpolation: {
      escapeValue: false,
    },
  });
};

export default i18n;

export const getDisabledDates = (dates?: SearchDate[]) => {
  if (!dates?.length) {
    return [];
  }
  const days = buildAllDates(dates[0].date, dates[dates.length - 1].date);
  return days.filter((day) => {
    return !dates.some((date) => {
      return (
        day.getDate() === date.date.getDate() &&
        day.getMonth() === date.date.getMonth() &&
        day.getFullYear() === date.date.getFullYear()
      );
    });
  });
};

export const getSortedPackages = (packages: PackageSearch[], sort: string) => {
  switch (sort) {
    case 'priceIncrease': {
      return packages.sort((a, b) => a.salePrice - b.salePrice);
    }
    case 'priceDecrease': {
      return packages.sort((a, b) => b.salePrice - a.salePrice);
    }
    case 'starIncrease': {
      return packages.sort((a, b) => parseInt(a.hotelRating) - parseInt(b.hotelRating));
    }
    case 'starDecrease': {
      return packages.sort((a, b) => parseInt(b.hotelRating) - parseInt(a.hotelRating));
    }
    case 'nameDecrease': {
      return packages.sort((a, b) => b.rowName.localeCompare(a.rowName));
    }
    default: {
      return packages.sort((a, b) => a.rowName.localeCompare(b.rowName));
    }
  }
};

const buildAllDates = (startDate: Date, endDate: Date) => {
  const dates = [];
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

type FilterPredicate<T> = (item: T) => boolean;

type FilterItem = {
  checked: boolean;
  count?: number;
  name?: string;
  minPrice?: number;
  disabled?: boolean;
  packagesCount?: number;
};

const createFilter = <T>(
  items: FilterItem[],
  predicate: (item: T, filterItem: FilterItem) => boolean,
): FilterPredicate<T> => {
  const hasCheckedItems = items.some((item) => item.checked);
  return (item: T) =>
    !hasCheckedItems ||
    items.some((filterItem) => filterItem.checked && predicate(item, filterItem));
};

export const getBoardName = (name: string) => {
  if (name === 'All Inclusive') return 'Full Board';
  if (name === 'הכול כלול') return 'פנסיון מלא';

  return name;
};

export const getFilteredPackages = (
  newFilter: PackageFilterModel,
  packages?: PackageSearch[],
): PackageSearch[] => {
  if (!packages) return [];

  const filterByName: FilterPredicate<PackageSearch> = (pac) =>
    pac.rowName.toLowerCase().includes(newFilter.name.value.toLowerCase());

  const filterByProvider: FilterPredicate<PackageSearch> = (pac) =>
    !newFilter.provider.value || newFilter.provider.value === pac.provider;

  const filterByPrice: FilterPredicate<PackageSearch> = (pac) =>
    newFilter.price.value[0] <= pac.salePrice && newFilter.price.value[1] >= pac.salePrice;

  const filterByStars = createFilter<PackageSearch>(
    newFilter.stars,
    (pac, star) => parseInt(pac.hotelRating) === star.count,
  );

  const filterByNights: FilterPredicate<PackageSearch> = (pac) =>
    newFilter.nights
      .filter((night) => night.checked)
      .some((night) => parseInt(pac.dealLength) === night.count);

  const filterByBoards = createFilter<PackageSearch>(
    newFilter.boards,
    (pac, board) => pac.boardingDescription === board.name,
  );

  const filterByDestination = createFilter<PackageSearch>(
    newFilter.destination,
    (pac, dest) => (pac.cityFullDescription || pac.dealDestination) === dest.name,
  );

  const filterByDestinationReturn = createFilter<PackageSearch>(
    newFilter.destinationReturn ?? [],
    (pac, airport) => pac.dealDestinationReturn === airport.name,
  );

  const filterByFeatures = createFilter<PackageSearch>(newFilter.features, (pac, feat) =>
    pac.listOfDealFeature.some((deal: string) =>
      deal.toLowerCase().includes(feat.name?.toLowerCase() || ''),
    ),
  );

  const filterByFastOK: FilterPredicate<PackageSearch> = (pac) => !newFilter.fastOK || pac.fastOK;


  return packages.map((pac) => {
    const visibleNights = filterByNights(pac);
    const visibleName = filterByName(pac);
    const visibleProvider = filterByProvider(pac);
    const visiblePrice = filterByPrice(pac);
    const visibleStars = filterByStars(pac);
    const visibleBoards = filterByBoards(pac);
    const visibleDestination = filterByDestination(pac);
    const visibleFeatures = filterByFeatures(pac);
    const visibleFastOK = filterByFastOK(pac);
    const visibleDestinationReturn = filterByDestinationReturn(pac);
    const visible =
      visibleName &&
      visibleProvider &&
      visiblePrice &&
      visibleStars &&
      visibleBoards &&
      visibleDestination &&
      visibleFeatures &&
      visibleFastOK &&
      visibleDestinationReturn;

    return {
      ...pac,
      visible: visible && visibleNights,
    };
  });
};

export const getFlight = (options: Option[], index: number = 0) => {
  if (options?.length && options[0].flights?.length) {
    return options[0].flights[index];
  }
};

export const getSeats = (options: Option[], flights: FlightModel[]) => {
  if (options?.length && options[0].flights?.length) {
    return options[0].flights[0].outbound.seats;
  } else if (flights?.[0]?.pairs?.[0]?.toSeats) {
    return parseInt(flights[0].pairs[0].toSeats);
  } else {
    return 9;
  }
};

export const getInfantPrice = (options: Option[]) => {
  if (options?.length && options[0].prices?.length) {
    return +options[0].prices[0].infantPrice;
  }

  return 0;
};

export const formatOccupancySrt = (complect: string) => {
  const adult = complect.match(new RegExp('A', 'g'))?.length;
  const child = complect.match(new RegExp('C', 'g'))?.length;
  const res = [
    { label: `${adult} ${i18n.t('search.adults')}`, value: adult },
    { label: `${child} ${i18n.t('search.children')}`, value: child },
  ];
  return res
    .filter((item) => item.value)
    .map((item) => item.label)
    .join(', ');
};

export const getFare = (fareBasis: string, list: { [key: string]: string }) => {
  let fareStr = '';
  for (let key in list) {
    if (fareBasis === key) {
      fareStr = list[key];
    }
  }
  return fareStr ? `(${getBoardName(fareStr)})` : '';
};

export const getDealType = (dealType: string, type: number, location?: string) => {
  if (location?.includes('tour')) {
    return 'טיול מאורגן';
  }
  if (type === 1) {
    return dealType.replace('NOFSHON', 'TOURS');
  }

  return dealType;
};

export const formatOccupancySrtByDeal = (deal: Deal) => {
  const res = [
    { label: `${deal.adT_Num} ${i18n.t('search.adults')}`, value: deal.adT_Num },
    { label: `${deal.cnN_Num} ${i18n.t('search.children')}`, value: deal.cnN_Num },
    { label: `${deal.inF_Num} ${i18n.t('search.infants')}`, value: deal.inF_Num },
  ];
  return res
    .filter((item) => item.value)
    .map((item) => item.label)
    .join(', ');
};
export const formatOccupancySrtByPaxes = (paxes: Pax[]) => {
  const adults = paxes.filter((pax) => pax.paxTypeCode === PassengerType.Adult)?.length;
  const cnn = paxes.filter((pax) => pax.paxTypeCode === PassengerType.Child)?.length;
  const infants = paxes.filter((pax) => pax.paxTypeCode === PassengerType.Infant)?.length;
  const res = [
    { label: `${adults} ${i18n.t('search.adults')}`, value: adults },
    { label: `${cnn} ${i18n.t('search.children')}`, value: cnn },
    { label: `${infants} ${i18n.t('search.infants')}`, value: infants },
  ];
  return res
    .filter((item) => item.value)
    .map((item) => item.label)
    .join(', ');
};

export const onImageLoadError = (
  { currentTarget }: SyntheticEvent<HTMLImageElement, Event>,
  path: any,
) => {
  currentTarget.onerror = null;
  currentTarget.src = path;
};
